import React from 'react';
import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import { PersonOutlineOutlined } from '@material-ui/icons';
import { useField } from 'react-final-form';

import { Group, GroupContent, GroupItem, GroupTitle } from 'components/common/Group';
import { Checkbox, Input, Select, validator } from 'components/form';
import { ToLocation } from 'icons';
import { CountryDTO } from '../../types';
import StateOptionsField from './StateOptionsField';
import { DictionaryStatus } from './types';

const useStyles = makeStyles((theme) => ({
  groupItem: {
    '@media (max-width: 600px)': {
      flex: '0 0 100%',
      maxWidth: '100%',
    },
  },
  dictionaryStatus: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: theme.spacing(8),
  },
  dictionaryError: {
    color: theme.palette.error.main,
  },
}));

const requiredTrimmed = (value) =>
  typeof value === 'string' && value.trim() ? undefined : validator.required(value);

const validateFullName = validator.composeValidators(requiredTrimmed, validator.maxLength(35));
const validateAddressLine1 = validator.composeValidators(requiredTrimmed, validator.maxLength(50));
const validateOptionalAddressLine = validator.maxLength(50);

const RerouteContactFields = ({
  countries,
  countriesLoading,
  countriesError,
  onStateStatusChange,
}: {
  countries: CountryDTO[];
  countriesLoading: boolean;
  countriesError: any;
  onStateStatusChange: (status: DictionaryStatus) => void;
}) => {
  const classes = useStyles();
  const {
    input: { value: countryCode },
  } = useField('newContact.address.countryCode');

  if (countriesLoading) {
    return (
      <div className={classes.dictionaryStatus}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (countriesError) {
    return (
      <Typography className={classes.dictionaryError} variant="body2">
        Unable to load countries
      </Typography>
    );
  }

  const country = countries.find((item) => item.code === countryCode);
  const stateRequired = countryCode === 'US' || countryCode === 'CA';
  const postalCodeRequired = Boolean(country?.postalCodeRequired);
  const validatePhone = countryCode === 'US' ? () => undefined : requiredTrimmed;
  const validateState = stateRequired ? requiredTrimmed : () => undefined;
  const validatePostalCode = postalCodeRequired ? requiredTrimmed : () => undefined;

  return (
    <>
      <Group>
        <GroupTitle icon={<PersonOutlineOutlined />}>Contact</GroupTitle>
        <GroupContent>
          <GroupItem wide>
            <Input name="newContact.name" label="Full Name" validate={validateFullName} />
          </GroupItem>
          <GroupItem wide>
            <Input name="newContact.companyName" label="Company Name (optional)" />
          </GroupItem>
          <GroupItem className={classes.groupItem}>
            <Input
              name="newContact.phoneNumber"
              label={countryCode === 'US' ? 'Phone (optional)' : 'Phone'}
              validate={validatePhone}
            />
          </GroupItem>
          <GroupItem className={classes.groupItem}>
            <Input
              name="newContact.emailAddress"
              label="Email (optional)"
              validate={validator.email}
            />
          </GroupItem>
          <GroupItem className={classes.groupItem}>
            <Input name="newContact.taxIdentificationNumber" label="Tax number (optional)" />
          </GroupItem>
        </GroupContent>
      </Group>
      <Group>
        <GroupTitle icon={<ToLocation />}>Address</GroupTitle>
        <GroupContent>
          <GroupItem className={classes.groupItem}>
            <Input
              name="newContact.address.addressLine1"
              label="Street Address 1"
              validate={validateAddressLine1}
            />
          </GroupItem>
          <GroupItem className={classes.groupItem}>
            <Input
              name="newContact.address.addressLine2"
              label="Street Address 2 (optional)"
              validate={validateOptionalAddressLine}
            />
          </GroupItem>
          <GroupItem wide>
            <Input
              name="newContact.address.addressLine3"
              label="Street Address 3 (optional)"
              validate={validateOptionalAddressLine}
            />
          </GroupItem>
          <GroupItem className={classes.groupItem}>
            <Input name="newContact.address.city" label="City/Town" validate={requiredTrimmed} />
          </GroupItem>
          <GroupItem className={classes.groupItem}>
            {country?.id ? (
              <StateOptionsField
                key={country.id}
                countryId={country.id}
                name="newContact.address.stateProvinceCode"
                validate={validateState}
                onStatusChange={onStateStatusChange}
              />
            ) : (
              <Select
                name="newContact.address.stateProvinceCode"
                label="State"
                options={[]}
                disabled
              />
            )}
          </GroupItem>
          <GroupItem className={classes.groupItem}>
            <Input
              name="newContact.address.zipCode"
              label={postalCodeRequired ? 'ZIP' : 'ZIP (optional)'}
              validate={validatePostalCode}
            />
          </GroupItem>
          <GroupItem className={classes.groupItem}>
            <Select
              name="newContact.address.countryCode"
              label="Country"
              options={countries.map((item) => ({ value: item.code, label: item.name }))}
              validate={requiredTrimmed}
            />
          </GroupItem>
          <GroupItem wide>
            <Checkbox name="newContact.address.residential" label="Residential address" />
          </GroupItem>
        </GroupContent>
      </Group>
    </>
  );
};

export default RerouteContactFields;
